"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Zap, Shield, Flower, Crown, Mountain } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LaForcePage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-orange-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">Tarot</span>
            <span className="text-orange-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-orange-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-orange-500">/</span>
            <span className="text-sm">La Force</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/marseille"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot de Marseille
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Major Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">La Force</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-orange-900/20 to-red-900/20 rounded-xl p-8 mb-8 border border-orange-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-la-force.svg"
                  alt="La Force Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-orange-900/40 to-red-900/40 border border-orange-500/30 flex-col items-center justify-center text-orange-300">
                  <span className="text-3xl font-bold mb-2">XI</span>
                  <span className="text-lg font-semibold">LA FORCE</span>
                  <span className="text-sm text-center mt-2">Strength</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XI</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">La Force</h1>
                  <p className="text-xl text-orange-400">İç Güç / Cesaret / Kontrol</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde on birinci kart. İç gücü, cesareti ve kendini kontrol etmeyi
                temsil eder. Bu kart, şiddet yerine sevgi ve sabrı kullanarak gücü elde etmeyi gösterir.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">İç Güç</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Cesaret</span>
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">Kontrol</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Sabır</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-orange-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İç Güç</h3>
                  <p className="text-slate-400 text-sm">Manevi güç, iç kararlılık ve özgüven.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sabırlı Kontrol</h3>
                  <p className="text-slate-400 text-sm">Güçlü durumları sabırla yönetmek.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sevgi ile Zafer</h3>
                  <p className="text-slate-400 text-sm">Şiddet yerine sevgi kullanarak kazanmak.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Cesaret</h3>
                  <p className="text-slate-400 text-sm">Zorlukları aşma kararlılığı ve yiğitlik.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Güçsüzlük</h3>
                  <p className="text-slate-400 text-sm">İç güç eksikliği, özgüven kaybı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Agresiflik</h3>
                  <p className="text-slate-400 text-sm">Aşırı saldırganlık, kontrolsüz öfke.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sabırsızlık</h3>
                  <p className="text-slate-400 text-sm">Acele etme, dürtüsel davranışlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Zorbalık</h3>
                  <p className="text-slate-400 text-sm">Gücü kötüye kullanma, baskıcı davranış.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marseille Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🦁</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Aslan</h3>
              <p className="text-slate-400 text-sm">Ham güç, cesaret ve vahşi doğa.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👩</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kadın Figürü</h3>
              <p className="text-slate-400 text-sm">Sabır, sevgi ve nazik güç.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Sonsuzluk Simgesi</h3>
              <p className="text-slate-400 text-sm">Başının üstündeki sonsuzluk işareti.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Flower className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Çiçekler</h3>
              <p className="text-slate-400 text-sm">Güzellik, nezaket ve doğal güç.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🤲</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Eller</h3>
              <p className="text-slate-400 text-sm">Aslanın ağzını nazikçe açan eller.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mountain className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Denge</h3>
              <p className="text-slate-400 text-sm">Güç ve zarafet arasında mükemmel denge.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Sabırlı ve anlayışlı yaklaşım</li>
                <li>• Sevgi ile zorlukları aşma</li>
                <li>• İlişkide güçlü duruş</li>
                <li>• Karşılıklı saygı</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Liderlik pozisyonları</li>
                <li>• Zorlukları sabırla aşma</li>
                <li>• Ekip yönetimi</li>
                <li>• Güçlü iş disiplini</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Öz-kontrol geliştirme</li>
                <li>• İç güçlü keşfetme</li>
                <li>• Sabır kazanma</li>
                <li>• Cesaret geliştirme</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-400" />
                Sağlık ve Refah
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Güçlü immün sistem</li>
                <li>• Fiziksel dayanıklılık</li>
                <li>• Mental güç</li>
                <li>• İyileşme süreci</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel Marseille tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-roue-de-fortune"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            La Roue de Fortune
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/le-pendu"
            className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            Le Pendu
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}